import React, { useMemo, useState } from "react";
import { Collapse, Tag, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const TEAM_NONE = "_unteamed";

// Human-readable copy for the algorithm's bypass reasons.
const BYPASS_REASON_LABEL = {
  role_excluded: "Roli i përjashtuar (mbikëqyrës / kontrollor)",
  policy_excluded: "Përjashtuar nga politika",
  month_override: "Përjashtuar për këtë muaj",
};
const BYPASS_REASON_COLOR = {
  role_excluded: "purple",
  policy_excluded: "geekblue",
  month_override: "orange",
};

// Read-only visualization of the schedule's per-employee afternoon
// distribution. Source of truth is `schedule.fairnessReport` produced
// by the rotation algorithm. Falls back to recomputing from the raw
// `weeks[]` if the backend hasn't included a fairness report yet
// (older schedules).
function FairnessPanel({ schedule, employees = [] }) {
  const [teamFilter, setTeamFilter] = useState([]);

  const report = useMemo(() => {
    if (schedule?.fairnessReport?.entries) return schedule.fairnessReport;
    return computeFallback(schedule, employees);
  }, [schedule, employees]);

  // Per-employee count of "inherited from previous month" afternoon
  // weeks. These count toward `thisMonth` (so the monthly cap is
  // respected) but the UI annotates them so admins understand that the
  // actual rotation slot was claimed in the previous month.
  const inheritedByEmployee = useMemo(() => {
    const aShift = schedule?.afternoonShiftIdSnapshot;
    const map = new Map();
    for (const w of schedule?.weeks || []) {
      for (const a of w.assignments || []) {
        if (a.shiftId === aShift && a.inheritedFromYearMonth) {
          map.set(
            a.employeeId,
            (map.get(a.employeeId) || 0) + 1
          );
        }
      }
    }
    return map;
  }, [schedule]);

  // Per-employee count of cross-team afternoon weeks. The employee was
  // borrowed by another team to cover a quota their own team couldn't
  // fill alone. Shown as a small "→ Team" annotation next to thisMonth.
  const borrowedByEmployee = useMemo(() => {
    const aShift = schedule?.afternoonShiftIdSnapshot;
    const map = new Map();
    for (const w of schedule?.weeks || []) {
      for (const a of w.assignments || []) {
        if (
          a.shiftId === aShift &&
          a.borrowedFromTeam &&
          a.borrowedForTeam &&
          a.borrowedFromTeam !== a.borrowedForTeam
        ) {
          const prev = map.get(a.employeeId) || { count: 0, teams: new Set() };
          prev.count += 1;
          prev.teams.add(a.borrowedForTeam);
          map.set(a.employeeId, prev);
        }
      }
    }
    return map;
  }, [schedule]);

  const totalBorrows = useMemo(() => {
    let n = 0;
    for (const v of borrowedByEmployee.values()) n += v.count;
    return n;
  }, [borrowedByEmployee]);

  const teamOptions = useMemo(() => {
    const set = new Set(report.entries.map((e) => e.team || TEAM_NONE));
    return Array.from(set)
      .sort()
      .map((t) => ({
        value: t,
        label: t === TEAM_NONE ? "Pa ekip" : t,
      }));
  }, [report.entries]);

  const filteredEntries = useMemo(() => {
    if (!teamFilter.length) return report.entries;
    const allow = new Set(teamFilter);
    return report.entries.filter((e) => allow.has(e.team || TEAM_NONE));
  }, [report.entries, teamFilter]);

  const max12mo = useMemo(
    () =>
      report.entries.reduce(
        (m, e) => Math.max(m, e.afternoonWorkingDays12mo || 0),
        0
      ),
    [report.entries]
  );

  return (
    <Collapse
      ghost
      items={[
        {
          key: "fairness",
          label: (
            <div className="fairness-panel__header">
              <span className="fairness-panel__title">
                Drejtësia e rotacionit
              </span>
              <Tooltip
                title={
                  "Numëron sa javë pasdite ka pasur secili punonjës këtë muaj " +
                  "dhe 12 muaj e fundit. Outlier-ët (të theksuar me kuq) janë " +
                  "punonjësit që kanë tejkaluar dukshëm mesataren dhe kufirin " +
                  "mujor të caktuar nga politika."
                }
              >
                <InfoCircleOutlined style={{ color: "#888" }} />
              </Tooltip>
              <span className="fairness-panel__meta">
                Mesatare: <b>{report.avgThisMonth ?? 0}</b> javë/punonjës ·
                Kufi: <b>{report.monthlyCap ?? "—"}</b> · Pjesëmarrës:{" "}
                <b>{report.participantsThisMonth ?? 0}</b>
              </span>
              {report.outlierCount > 0 ? (
                <Tag color="red">{report.outlierCount} outlier</Tag>
              ) : (
                <Tag color="green">balancuar</Tag>
              )}
              {report.bypassedCount > 0 ? (
                <Tag color="purple">{report.bypassedCount} bypass</Tag>
              ) : null}
              {totalBorrows > 0 ? (
                <Tooltip
                  title={
                    "Sa caktime pasdite janë mbuluar duke marrë hua punonjës " +
                    "nga ekipi tjetër (cross-team relief), kur ekipi vendas " +
                    "nuk kishte më të kandidatëve pa tejkaluar kufirin mujor."
                  }
                >
                  <Tag color="orange">{totalBorrows} cross-team</Tag>
                </Tooltip>
              ) : null}
            </div>
          ),
          children: (
            <>
              {teamOptions.length > 1 ? (
                <Select
                  mode="multiple"
                  allowClear
                  size="small"
                  placeholder="Filtro sipas ekipit"
                  value={teamFilter}
                  onChange={setTeamFilter}
                  options={teamOptions}
                  style={{ minWidth: 240, marginBottom: 8 }}
                />
              ) : null}

              <div className="fairness-panel__rows">
                <div className="fairness-panel__head-cell">Punonjësi</div>
                <div className="fairness-panel__head-cell">Roli · Ekipi</div>
                <div className="fairness-panel__head-cell" style={{ textAlign: "right" }}>Këtë muaj</div>
                <div className="fairness-panel__head-cell" style={{ textAlign: "right" }}>12 muaj</div>
                <div className="fairness-panel__head-cell">Shpërndarja 12 muaj</div>

                {filteredEntries.map((e) => {
                  const pct = max12mo > 0
                    ? Math.round((e.afternoonWorkingDays12mo / max12mo) * 100)
                    : 0;
                  const inheritedCount = inheritedByEmployee.get(e.employeeId) || 0;
                  const borrowInfo = borrowedByEmployee.get(e.employeeId);
                  return (
                    <React.Fragment key={e.employeeId}>
                      <div className={e.isOutlier ? "fairness-panel__row--outlier" : ""}>
                        {e.employeeName}
                      </div>
                      <div style={{ color: "#888" }}>
                        {e.roleName || "—"}
                        {e.team && e.team !== TEAM_NONE ? ` · ${e.team}` : ""}
                      </div>
                      <div
                        className={e.isOutlier ? "fairness-panel__row--outlier" : ""}
                        style={{ textAlign: "right" }}
                      >
                        {e.thisMonth}
                        {inheritedCount > 0 ? (
                          <Tooltip
                            title={`${inheritedCount} javë e trashëguar nga muaji paraardhës (vazhdim i bllokut javor mes muajve).`}
                          >
                            <span
                              style={{
                                marginLeft: 4,
                                color: "#1d39c4",
                                fontSize: 10,
                              }}
                            >
                              (i)
                            </span>
                          </Tooltip>
                        ) : null}
                        {borrowInfo ? (
                          <Tooltip
                            title={`${borrowInfo.count} javë i marrë hua nga ekip${
                              borrowInfo.teams.size > 1 ? "et" : "i"
                            } tjetër (${Array.from(borrowInfo.teams).join(", ")}).`}
                          >
                            <span
                              style={{
                                marginLeft: 4,
                                color: "#b86d00",
                                fontSize: 10,
                              }}
                            >
                              ↔
                            </span>
                          </Tooltip>
                        ) : null}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {e.afternoonWeeks12mo}
                      </div>
                      <div className="fairness-panel__bar-track">
                        <div
                          className={`fairness-panel__bar-fill ${
                            e.isOutlier ? "fairness-panel__bar-fill--outlier" : ""
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {Array.isArray(report.bypassed) && report.bypassed.length > 0 ? (
                <div className="fairness-panel__bypass">
                  <div className="fairness-panel__bypass-title">
                    Mbikëqyrës / bypass
                    <Tooltip
                      title={
                        "Këta punonjës nuk hyjnë në llogaritjen e rotacionit. " +
                        "Ata mund të shohin orarin për kontroll dhe mbikëqyrje, " +
                        "por nuk u caktohet asnjë turn nga algoritmi."
                      }
                    >
                      <InfoCircleOutlined
                        style={{ marginLeft: 6, color: "#888" }}
                      />
                    </Tooltip>
                  </div>
                  <div className="fairness-panel__bypass-list">
                    {report.bypassed.map((b) => (
                      <Tag
                        key={b.employeeId}
                        color={BYPASS_REASON_COLOR[b.reason] || "default"}
                        style={{ marginBottom: 4 }}
                      >
                        <b>{b.employeeName}</b>
                        <span style={{ opacity: 0.85, marginLeft: 6 }}>
                          {b.roleName || "—"}
                          {b.team && b.team !== TEAM_NONE
                            ? ` · ${b.team}`
                            : ""}
                        </span>
                        <span style={{ opacity: 0.7, marginLeft: 6 }}>
                          ({BYPASS_REASON_LABEL[b.reason] || b.reason})
                        </span>
                      </Tag>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ),
        },
      ]}
    />
  );
}

// Fallback: derive a basic per-employee count from a legacy schedule
// that doesn't carry a fairnessReport.
function computeFallback(schedule, employees) {
  const aShift = schedule?.afternoonShiftIdSnapshot;
  const empById = new Map(employees.map((e) => [e.employeeId, e]));
  const counts = new Map();
  for (const w of schedule?.weeks || []) {
    for (const a of w.assignments || []) {
      if (a.shiftId === aShift) {
        counts.set(a.employeeId, (counts.get(a.employeeId) || 0) + 1);
      }
    }
  }
  const ids = new Set();
  for (const w of schedule?.weeks || []) {
    for (const a of w.assignments || []) ids.add(a.employeeId);
  }
  const entries = Array.from(ids).map((eid) => {
    const emp = empById.get(eid) || {};
    return {
      employeeId: eid,
      employeeName: `${emp.employeeFirstName || ""} ${emp.employeeLastName || ""}`.trim(),
      team: emp.employeeTeam || TEAM_NONE,
      roleName: emp.employeeRoleName || "",
      thisMonth: counts.get(eid) || 0,
      afternoonWeeks12mo: counts.get(eid) || 0,
      afternoonWorkingDays12mo: counts.get(eid) || 0,
      isOutlier: false,
    };
  });
  entries.sort((a, b) => b.thisMonth - a.thisMonth);
  const total = entries.reduce((s, e) => s + e.thisMonth, 0);
  const participants = entries.filter((e) => e.thisMonth > 0).length;
  return {
    monthlyCap: 1,
    avgThisMonth: participants > 0 ? Number((total / participants).toFixed(2)) : 0,
    participantsThisMonth: participants,
    totalThisMonth: total,
    outlierCount: 0,
    entries,
  };
}

export default FairnessPanel;
